/**
 * client/familiars.ts
 * Responsabilidad: Definición de familiares (mascotas cosméticas),
 * sistema de partículas de estela elemental con ciclo de vida estricto y cálculo de órbita bobbing.
 */

export type FamiliarElement = "water" | "fire" | "plant" | "electric" | "earth";

export interface FamiliarDef {
    id: string;
    element: FamiliarElement;
    tier: number;
    name: string;
    priceOrbs: number;
    priceCrystals: number;
    buffType: string;
    baseValue: number;
    affinityBonus: number;
    description: string;
    assetPath: string;
    rarity?: "rare" | "epic" | "exclusive" | string;
}

export const FALLBACK_FAMILIARS: readonly FamiliarDef[] = [
    // Agua (Velocidad: Tier 1: 0.03/0.01, Tier 2: 0.05/0.02, Tier 3: 0.08/0.03)
    { id: "agua1", element: "water", tier: 1, name: "Aqualing Menor", priceOrbs: 15, priceCrystals: 1500, rarity: "rare", assetPath: "assets/familiars/agua1.png", buffType: "speed", baseValue: 0.03, affinityBonus: 0.01, description: "+3% Vel (+1% Afinidad Agua)" },
    { id: "agua2", element: "water", tier: 2, name: "Leviatán Ondulante", priceOrbs: 40, priceCrystals: 4000, rarity: "epic", assetPath: "assets/familiars/agua2.png", buffType: "speed", baseValue: 0.05, affinityBonus: 0.02, description: "+5% Vel (+2% Afinidad Agua)" },
    { id: "agua3", element: "water", tier: 3, name: "Soberano Abisal", priceOrbs: 100, priceCrystals: 10000, rarity: "exclusive", assetPath: "assets/familiars/agua3.png", buffType: "speed", baseValue: 0.08, affinityBonus: 0.03, description: "+8% Vel (+3% Afinidad Agua)" },
    // Fuego (Propulsión: Tier 1: 0.10/0.05, Tier 2: 0.18/0.07, Tier 3: 0.28/0.12)
    { id: "fuego1", element: "fire", tier: 1, name: "Chispita Ígnea", priceOrbs: 15, priceCrystals: 1500, rarity: "rare", assetPath: "assets/familiars/fuego1.png", buffType: "boost_drain", baseValue: 0.10, affinityBonus: 0.05, description: "-10% Consumo Boost (+5% Afinidad)" },
    { id: "fuego2", element: "fire", tier: 2, name: "Salamandra de Fuego", priceOrbs: 40, priceCrystals: 4000, rarity: "epic", assetPath: "assets/familiars/fuego2.png", buffType: "boost_drain", baseValue: 0.18, affinityBonus: 0.07, description: "-18% Consumo Boost (+7% Afinidad)" },
    { id: "fuego3", element: "fire", tier: 3, name: "Fénix Magmático", priceOrbs: 100, priceCrystals: 10000, rarity: "exclusive", assetPath: "assets/familiars/fuego3.png", buffType: "boost_drain", baseValue: 0.28, affinityBonus: 0.12, description: "-28% Consumo Boost (+12% Afinidad)" },
    // Planta (Comida: Tier 1: 1/1, Tier 2: 2/1, Tier 3: 4/2)
    { id: "planta1", element: "plant", tier: 1, name: "Brote Silvestre", priceOrbs: 15, priceCrystals: 1500, rarity: "rare", assetPath: "assets/familiars/planta1.png", buffType: "food_gain", baseValue: 1.0, affinityBonus: 1.0, description: "+1 Masa por comida (+1 Afinidad)" },
    { id: "planta2", element: "plant", tier: 2, name: "Espíritu Frondoso", priceOrbs: 40, priceCrystals: 4000, rarity: "epic", assetPath: "assets/familiars/planta2.png", buffType: "food_gain", baseValue: 2.0, affinityBonus: 1.0, description: "+2 Masa por comida (+1 Afinidad)" },
    { id: "planta3", element: "plant", tier: 3, name: "Guardián Primigenio", priceOrbs: 100, priceCrystals: 10000, rarity: "exclusive", assetPath: "assets/familiars/planta3.png", buffType: "food_gain", baseValue: 4.0, affinityBonus: 2.0, description: "+4 Masa por comida (+2 Afinidad)" },
    // Electricidad (Imán: Tier 1: 60/25, Tier 2: 100/40, Tier 3: 150/60)
    { id: "electricidad1", element: "electric", tier: 1, name: "Voltio Chispeante", priceOrbs: 15, priceCrystals: 1500, rarity: "rare", assetPath: "assets/familiars/electricidad1.png", buffType: "magnet_radius", baseValue: 60.0, affinityBonus: 25.0, description: "+60px Radio Imán (+25px Afinidad)" },
    { id: "electricidad2", element: "electric", tier: 2, name: "Plasma Relampagueante", priceOrbs: 40, priceCrystals: 4000, rarity: "epic", assetPath: "assets/familiars/electricidad2.png", buffType: "magnet_radius", baseValue: 100.0, affinityBonus: 40.0, description: "+100px Radio Imán (+40px Afinidad)" },
    { id: "electricidad3", element: "electric", tier: 3, name: "Tempestad Viva", priceOrbs: 100, priceCrystals: 10000, rarity: "exclusive", assetPath: "assets/familiars/electricidad3.png", buffType: "magnet_radius", baseValue: 150.0, affinityBonus: 60.0, description: "+150px Radio Imán (+60px Afinidad)" },
    // Tierra (Resistencia: Tier 1: 0.08/0.04, Tier 2: 0.14/0.06, Tier 3: 0.22/0.10)
    { id: "tierra1", element: "earth", tier: 1, name: "Piedrecita Ámbar", priceOrbs: 15, priceCrystals: 1500, rarity: "rare", assetPath: "assets/familiars/tierra1.png", buffType: "damage_resistance", baseValue: 0.08, affinityBonus: 0.04, description: "+8% Resistencia (+4% Afinidad)" },
    { id: "tierra2", element: "earth", tier: 2, name: "Gólem Rocoso", priceOrbs: 40, priceCrystals: 4000, rarity: "epic", assetPath: "assets/familiars/tierra2.png", buffType: "damage_resistance", baseValue: 0.14, affinityBonus: 0.06, description: "+14% Resistencia (+6% Afinidad)" },
    { id: "tierra3", element: "earth", tier: 3, name: "Coloso Ancestral", priceOrbs: 100, priceCrystals: 10000, rarity: "exclusive", assetPath: "assets/familiars/tierra3.png", buffType: "damage_resistance", baseValue: 0.22, affinityBonus: 0.10, description: "+22% Resistencia (+10% Afinidad)" },
];

export const FAMILIAR_MAP: ReadonlyMap<string, FamiliarDef> = new Map(
    FALLBACK_FAMILIARS.map((f) => [f.id, f])
);

export function getFamiliarDef(id: string): FamiliarDef | undefined {
    return FAMILIAR_MAP.get(id);
}

export function getFamiliarElement(id: string): FamiliarElement {
    const def = FAMILIAR_MAP.get(id);
    if (def) return def.element;
    if (id.startsWith("agua")) return "water";
    if (id.startsWith("fuego")) return "fire";
    if (id.startsWith("planta")) return "plant";
    if (id.startsWith("electricidad")) return "electric";
    if (id.startsWith("tierra")) return "earth";
    return "water";
}

export interface FamiliarParticle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    color: string;
    colorHex: number;
    alpha: number;
    size: number;
    maxAge: number;
    age: number;
}

/** Paletas de partículas elementales para familiares */
const ELEMENT_PALETTES: Record<FamiliarElement, { strings: [string, string]; hex: [number, number] }> = {
    water: {
        strings: ["#38bdf8", "#0284c7"],
        hex: [0x38bdf8, 0x0284c7],
    },
    fire: {
        strings: ["#ef4444", "#f97316"],
        hex: [0xef4444, 0xf97316],
    },
    plant: {
        strings: ["#22c55e", "#15803d"],
        hex: [0x22c55e, 0x15803d],
    },
    electric: {
        strings: ["#facc15", "#fef08a"],
        hex: [0xfacc15, 0xfef08a],
    },
    earth: {
        strings: ["#d97706", "#78350f"],
        hex: [0xd97706, 0x78350f],
    },
};

/**
 * Sistema de partículas ultra-ligero para la estela de familiares.
 * REQUISITO CRÍTICO DE RENDIMIENTO:
 * - Cap estricto a 20 partículas máximo por sistema.
 * - Vida útil maxAge <= 600ms.
 * - Filtrado y liberación activa en cada update(dt).
 */
export class FamiliarParticleSystem {
    public static readonly MAX_PARTICLES = 20;
    private particles: FamiliarParticle[] = [];
    private emitTimer: number = 0;

    public getParticles(): readonly FamiliarParticle[] {
        return this.particles;
    }

    /**
     * Emite una partícula de estela elemental en la posición especificada.
     */
    public emit(x: number, y: number, element: FamiliarElement, count: number = 1): void {
        const palette = ELEMENT_PALETTES[element] || ELEMENT_PALETTES.water;

        if (this.particles.length >= FamiliarParticleSystem.MAX_PARTICLES) {
            this.particles.splice(0, count);
        }

        for (let i = 0; i < count; i++) {
            const colorIdx = Math.random() < 0.5 ? 0 : 1;
            const maxAge = 400 + Math.random() * 180; // 400-580ms (< 600ms)

            this.particles.push({
                x,
                y,
                vx: (Math.random() - 0.5) * 20,
                vy: (Math.random() - 0.5) * 20 - 5, // leve deriva ascendente
                color: palette.strings[colorIdx],
                colorHex: palette.hex[colorIdx],
                alpha: 0.85,
                size: 2.5 + Math.random() * 2.5,
                maxAge,
                age: 0,
            });
        }
    }

    /**
     * Actualiza el movimiento y vida de las partículas.
     * Elimina activamente aquellas cuyo age supere maxAge.
     */
    public update(dtMs: number): void {
        const dtSec = Math.min(0.1, dtMs / 1000);
        let writeIdx = 0;

        for (let i = 0; i < this.particles.length; i++) {
            const p = this.particles[i];
            p.age += dtMs;

            if (p.age < p.maxAge) {
                p.x += p.vx * dtSec;
                p.y += p.vy * dtSec;
                p.alpha = Math.max(0, 0.85 * (1 - p.age / p.maxAge));
                this.particles[writeIdx++] = p;
            }
        }

        // Truncar el array activamente (cero acumulación de memoria)
        this.particles.length = writeIdx;
    }

    /**
     * Vacía todas las partículas activas inmediatamente.
     */
    public clear(): void {
        this.particles.length = 0;
        this.emitTimer = 0;
    }
}

/**
 * Calcula la posición objetivo de órbita y flotación viva para el familiar:
 * - Ángulo base flotante (-Math.PI / 4) + oscilación sinusoidal sutil
 * - Distancia: playerRadius + 24px
 * - Oscilación vertical bobbing de 5px (Math.sin(nowMs * 0.005) * 5)
 */
export function getFamiliarTargetPosition(
    playerX: number,
    playerY: number,
    playerRadius: number,
    nowMs: number,
    isBuffActive: boolean = false
): { x: number; y: number } {
    if (isBuffActive) {
        const orbitSpeed = 0.007;
        const orbitAngle = nowMs * orbitSpeed;
        const orbitDistY = Math.max(26, playerRadius) + 22;
        const orbitDistX = orbitDistY * 1.35;
        return {
            x: playerX + Math.cos(orbitAngle) * orbitDistX,
            y: playerY + Math.sin(orbitAngle) * orbitDistY,
        };
    }
    // Posición normal de descanso (superior derecha)
    const baseAngle = -Math.PI / 4;
    const orbitSway = Math.sin(nowMs * 0.002) * 0.15;
    const orbitAngle = baseAngle + orbitSway;
    const distance = Math.max(22, playerRadius) + 26;
    const bobbing = Math.sin(nowMs * 0.005) * 5;
    return {
        x: playerX + Math.cos(orbitAngle) * distance,
        y: playerY + Math.sin(orbitAngle) * distance + bobbing,
    };
}

export function formatFamiliarDescription(fam: { buffType: string; baseValue: number; affinityBonus: number; description?: string }): string {
    const base = Number(fam.baseValue);
    const aff = Number(fam.affinityBonus);
    switch (fam.buffType) {
        case "speed":
            return `+${Math.round(base * 100)}% Vel (+${Math.round(aff * 100)}% Afinidad)`;
        case "boost_drain":
            return `-${Math.round(base * 100)}% Consumo Boost (+${Math.round(aff * 100)}% Afinidad)`;
        case "food_gain":
            return `+${Math.round(base)} Masa por comida (+${Math.round(aff)} Afinidad)`;
        case "magnet_radius":
            return `+${Math.round(base)}px Radio Imán (+${Math.round(aff)}px Afinidad)`;
        case "damage_resistance":
            return `+${Math.round(base * 100)}% Resistencia (+${Math.round(aff * 100)}% Afinidad)`;
        default:
            return fam.description || "";
    }
}
